interface RawRecommendations {
  clientProfile?: unknown;
  contentIdeas?: unknown;
  proposals?: unknown;
}

interface RecommendationsSource {
  customFields?: Record<string, unknown> | null;
  contacts?: Array<{ customFields?: Record<string, unknown> | null }> | null;
}

/** A recommendation and the observation it rests on. */
export interface RecommendationView {
  recommendation: string;
  /**
   * Absent only on leads enriched before the evidenced contract. The screen shows
   * the recommendation alone in that case rather than hiding history.
   */
  evidence?: string;
}

/** Who THIS account serves — not who they are — with its own reasoning. */
export interface ClientProfileView {
  audience: string;
  evidence?: string;
  priceSignals?: string;
  confidence?: 'High' | 'Medium' | 'Low';
}

/**
 * Advice about the account's own audience and business, as the lead detail
 * renders it (FLOSEAI-625).
 */
export interface InstagramRecommendationsView {
  clientProfile?: ClientProfileView;
  contentIdeas: RecommendationView[];
  proposals: RecommendationView[];
}

const CONFIDENCE_LEVELS: ReadonlyArray<NonNullable<ClientProfileView['confidence']>> = [
  'High',
  'Medium',
  'Low',
];

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Reads both shapes. The evidenced contract stores each item as
 * `{ recommendation, evidence }`; leads enriched before it stored a bare string,
 * and those rows are never rewritten, so dropping them would silently empty the
 * Analysis tab for every account already in the pipeline.
 */
function recommendations(value: unknown): RecommendationView[] {
  if (!Array.isArray(value)) return [];

  return value.flatMap((item) => {
    if (typeof item === 'string') {
      const recommendation = item.trim();
      return recommendation ? [{ recommendation }] : [];
    }

    if (!item || typeof item !== 'object') return [];
    const entry = item as Record<string, unknown>;
    const recommendation = text(entry.recommendation);
    if (!recommendation) return [];

    const evidence = text(entry.evidence);
    return [{ recommendation, ...(evidence && { evidence }) }];
  });
}

function clientProfile(value: unknown): ClientProfileView | undefined {
  // Legacy shape: one paragraph carrying the segment and nothing else.
  if (typeof value === 'string') {
    const audience = value.trim();
    return audience ? { audience } : undefined;
  }

  if (!value || typeof value !== 'object') return undefined;
  const entry = value as Record<string, unknown>;
  const audience = text(entry.audience);
  if (!audience) return undefined;

  const evidence = text(entry.evidence);
  const priceSignals = text(entry.priceSignals);
  const confidence = text(entry.confidence);

  return {
    audience,
    ...(evidence && { evidence }),
    ...(priceSignals && { priceSignals }),
    ...(CONFIDENCE_LEVELS.includes(confidence as NonNullable<ClientProfileView['confidence']>) && {
      confidence: confidence as NonNullable<ClientProfileView['confidence']>,
    }),
  };
}

function parse(raw: unknown): InstagramRecommendationsView | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as RawRecommendations;

  const profile = clientProfile(value.clientProfile);
  const contentIdeas = recommendations(value.contentIdeas);
  const proposals = recommendations(value.proposals);

  // Nothing usable means nothing to show — the tab hides rather than rendering
  // three empty headings.
  if (!profile && contentIdeas.length === 0 && proposals.length === 0) return null;

  return { ...(profile && { clientProfile: profile }), contentIdeas, proposals };
}

/**
 * Reads the recommendations the analysis produced, preferring the lead's own and
 * falling back to an enriched contact's — the same precedence the posts and reels
 * collectors use.
 */
export function collectInstagramRecommendations(
  lead: RecommendationsSource,
): InstagramRecommendationsView | null {
  const fromLead = parse(lead.customFields?.recommendations);
  if (fromLead) return fromLead;

  for (const contact of lead.contacts ?? []) {
    const fromContact = parse(contact.customFields?.recommendations);
    if (fromContact) return fromContact;
  }

  return null;
}
